import { registerSW } from 'virtual:pwa-register'

import { isNativeShell } from './native'
import { whileVisible } from './visibility'

/**
 * Service-worker and install plumbing.
 *
 * **A new build is applied by itself, at a moment nobody is using the page.**
 * It used to wait behind a "new version is ready" toast, which kept people on
 * old builds — the toast only appeared once the browser happened to look for a
 * new worker, and "Later" was one tap. The frontend and the API ship in one
 * image, so a stale page is an older frontend talking to a newer server.
 *
 * `registerType: 'prompt'` still decides *that* the worker waits; this module
 * decides *when* it takes over, because a reload under somebody's finger — in
 * the middle of typing a ZIP, or reading an alert — is worse than a day's
 * delay. It applies the moment any of these is true:
 *
 * - the page is hidden, so the reload happens where nobody sees it;
 * - nobody has touched the page since it was opened or came back to the
 *   front, which is the usual case — the check below runs on every return, so
 *   a phone picked up after a release updates before anyone has tapped;
 * - nobody has touched it for `IDLE_MS`, which is how a desktop tab that is
 *   never hidden still updates.
 *
 * The plain `virtual:pwa-register` entry point is used rather than the Svelte
 * one so this module owns runes instead of stores.
 */

/** Chromium-only; there is no lib.dom type for it. */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export const pwa = $state({
  offlineReady: false,
  canInstall: false,
  /**
   * A new build is installed and waiting. It still applies itself when the
   * page is hidden, untouched or idle (Phase 9); this is what lets the update
   * toast (round 2's 3.7) offer it *now* to somebody using the page.
   */
  updateReady: false,
})

/** A page nobody has touched for this long is a page nobody is using. */
export const IDLE_MS = 5 * 60_000
/**
 * How often an open, visible page asks for a new build — and it also asks on
 * every return. The browser's own check runs on *navigation*, and a PWA
 * resumed from the background never navigates, so without this a phone could
 * sit on one build for as long as iOS kept the app in memory. The request is
 * `sw.js`, served `no-cache`, and answered 304 when nothing changed.
 */
const UPDATE_CHECK_MS = 30 * 60_000

let updateSW: ((reload?: boolean) => Promise<void>) | null = null
let installEvent: BeforeInstallPromptEvent | null = null

/** A new worker is installed and waiting to take over. */
let waiting = false
let applying = false
/** Touched since the page was loaded or last came back to the front. */
let touched = false
let lastTouch = 0
let idleTimer: ReturnType<typeof setTimeout> | null = null

/**
 * Apply a waiting build if nobody is using the page; otherwise try again when
 * they stop. `updateSW(true)` tells the worker to skip waiting, and the page
 * reloads when it takes control — every open tab does, which is what keeps
 * two tabs from running two builds against one cache.
 */
function maybeApply(): void {
  if (!waiting || applying) return
  const idle = Date.now() - lastTouch
  if (document.hidden || !touched || idle >= IDLE_MS) {
    applying = true
    if (idleTimer) clearTimeout(idleTimer)
    void updateSW?.(true)
    return
  }
  if (idleTimer) clearTimeout(idleTimer)
  idleTimer = setTimeout(maybeApply, IDLE_MS - idle)
}

function onTouch(): void {
  touched = true
  lastTouch = Date.now()
  // Pushes the idle deadline back; a page in use is not idle.
  if (waiting) maybeApply()
}

function onVisibility(): void {
  // A return is a fresh look: nothing on screen has been touched yet.
  if (!document.hidden) touched = false
  maybeApply()
}

/** Call once, after `load`, so registration never competes with first paint. */
export function initPwa(): void {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault() // Suppress Chrome's own mini-infobar; we have a button.
    installEvent = e as BeforeInstallPromptEvent
    pwa.canInstall = true
  })
  window.addEventListener('appinstalled', () => {
    installEvent = null
    pwa.canInstall = false
  })

  // Deliberate input only. Not pointermove: a cursor resting on the map is
  // not somebody using it, and this runs on every event.
  for (const type of ['pointerdown', 'keydown', 'wheel'] as const) {
    window.addEventListener(type, onTouch, { capture: true, passive: true })
  }
  document.addEventListener('visibilitychange', onVisibility)

  updateSW = registerSW({
    immediate: true,
    onNeedRefresh() {
      waiting = true
      pwa.updateReady = true
      maybeApply()
    },
    onRegisteredSW(_url, registration) {
      if (!registration) return
      whileVisible(() => {
        // Offline this rejects, and there is nothing to do about it here.
        if (navigator.onLine) registration.update().catch(() => {})
      }, UPDATE_CHECK_MS)
    },
    onOfflineReady() {
      pwa.offlineReady = true
    },
    onRegisterError(error) {
      console.warn('service worker registration failed', error)
    },
  })
}

/** Take the waiting build now: the Reload on the update toast. */
export function applyUpdateNow(): void {
  if (!waiting || applying) return
  applying = true
  if (idleTimer) clearTimeout(idleTimer)
  void updateSW?.(true)
}

/** Tests only: the module keeps its update state for the life of the page. */
export function _resetPwa(): void {
  pwa.updateReady = false
  waiting = false
  applying = false
  touched = false
  lastTouch = 0
  if (idleTimer) clearTimeout(idleTimer)
  idleTimer = null
}

export async function promptInstall(): Promise<void> {
  if (!installEvent) return
  const event = installEvent
  installEvent = null
  pwa.canInstall = false
  await event.prompt()
}

export function isIos(): boolean {
  if (typeof navigator === 'undefined') return false
  // iPadOS 13+ reports as a Mac; the touch points give it away.
  return (
    /iphone|ipad|ipod/i.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  )
}

export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false
  // The iOS app is installed by definition: nothing in it may offer "Add to
  // Home Screen", and it is not a Safari tab with a separate storage.
  if (isNativeShell()) return true
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    // Safari's non-standard flag, still the only signal on iOS.
    (navigator as { standalone?: boolean }).standalone === true
  )
}
